import { useEffect, useRef } from "react";
import type { FilterKey } from "../types";
import { AddIcon, FilterIcon } from "./Icons";

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: "all", label: "All" },
  { key: "unanswered", label: "Unanswered" },
  { key: "pending", label: "Pending" },
  { key: "differences", label: "Differences" },
  { key: "notes", label: "Notes" },
  { key: "important", label: "Important" },
];

export function BrowseToolbar({
  browse,
  search,
  filter,
  focusSearch,
  onSearch,
  onFilter,
  onAdd,
}: {
  browse: boolean;
  search: string;
  filter: FilterKey;
  focusSearch: boolean;
  onSearch: (value: string) => void;
  onFilter: (value: FilterKey) => void;
  onAdd: () => void;
}) {
  const searchRef = useRef<HTMLInputElement>(null);
  const filterRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    if (browse && focusSearch) {
      searchRef.current?.focus({ preventScroll: true });
    }
  }, [browse, focusSearch]);

  const filterLabel =
    FILTERS.find((option) => option.key === filter)?.label ?? "All";

  return (
    <div className="browse-header-tools">
      <label className="browse-search-field">
        <span className="sr-only">Search the catalogue</span>
        <input
          ref={searchRef}
          type="search"
          value={search}
          aria-label="Search the catalogue"
          autoComplete="off"
          onChange={(event) => onSearch(event.target.value)}
        />
      </label>
      {browse ? (
        <>
          <details className="browse-filter-menu" ref={filterRef}>
            <summary
              className={filter === "all" ? "" : "selected"}
              aria-label={`Filter catalogue, current: ${filterLabel}`}
            >
              <FilterIcon />
            </summary>
            <div
              className="browse-filter-options"
              role="group"
              aria-label="Filter items"
            >
              {FILTERS.map((option) => (
                <button
                  type="button"
                  key={option.key}
                  className={filter === option.key ? "selected" : ""}
                  aria-pressed={filter === option.key}
                  onClick={() => {
                    onFilter(option.key);
                    if (filterRef.current) filterRef.current.open = false;
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </details>
          <button
            className="browse-tool-button browse-add-button"
            type="button"
            aria-label="Add a term to this room"
            onClick={onAdd}
          >
            <AddIcon />
          </button>
        </>
      ) : null}
    </div>
  );
}
